import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'rvincente_onboarding_v1_seen';

// Status sets kept in sync with /app/frontend/src/components/MatchRow.jsx
// because the API-Football status values come back as localized Italian
// labels (e.g. "Non iniziata", "Terminata") rather than the FIFA short codes.
const LIVE_STATUSES = ["1H", "2H", "HT", "LIVE", "ET", "BREAK", "1° Tempo", "2° Tempo", "Intervallo", "1st Half", "2nd Half", "Half Time"];
const FINISHED_STATUSES = ["FT", "AET", "PEN", "FT_PEN", "Full Time", "Terminata", "Finished", "After Extra Time", "Dopo supplementari", "Dopo rigori", "Ai rigori"];
const POSTPONED_STATUSES = ["PST", "Rinviata", "Postponed", "TBD", "Da definire", "To Be Defined"];
const CANCELLED_STATUSES = ["CANC", "Cancellata", "Cancelled", "ABD", "Abandoned", "Abbandonata", "AWD", "WO"];
const SUSPENDED_STATUSES = ["SUSP", "Sospesa", "Suspended", "INT", "Interrotta", "Interrupted"];

const isNotStarted = (m) => {
  if (!m) return false;
  const s = m.status;
  if (m.is_live === true) return false;
  if (m.is_finished === true) return false;
  if (LIVE_STATUSES.includes(s)) return false;
  if (FINISHED_STATUSES.includes(s)) return false;
  if (POSTPONED_STATUSES.includes(s)) return false;
  if (CANCELLED_STATUSES.includes(s)) return false;
  if (SUSPENDED_STATUSES.includes(s)) return false;
  return true;
};

const pickRandom = (arr) => (arr.length ? arr[Math.floor(Math.random() * arr.length)] : null);

// YYYY-MM-DD -> next-day YYYY-MM-DD (UTC-safe)
const nextDay = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
};

/**
 * OnboardingTutorial
 * Two-step first-visit walkthrough:
 *   1) If the currently selected day has NO upcoming matches (e.g. opened at
 *      23:30), highlight the NEXT day in the date pill bar.
 *   2) Once the user is on a day with upcoming matches, highlight a random
 *      not-yet-started match and force the user to tap it.
 * Click on the highlighted match marks the tutorial as "seen" forever (per
 * device, via localStorage).
 */
const OnboardingTutorial = ({ matches = [], isLoading = false, selectedDate = null }) => {
  // ───────── Lifecycle gates ─────────
  // Existing logged-in users (token already stored from a previous session)
  // should NOT see the tutorial — it's a first-install walkthrough, not a
  // re-engagement nudge. We auto-mark the flag so it never triggers again.
  const [seen, setSeen] = useState(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return true;
      // Heuristic: if there's an auth token, the user already knows the app.
      if (localStorage.getItem('auth_token')) {
        localStorage.setItem(STORAGE_KEY, '1');
        return true;
      }
      return false;
    } catch { return true; }
  });
  // Guests only: once registered/logged in (e.g. after the email-verification link
  // opened in another browser) the walkthrough never shows again.
  const { user, loading: authLoading } = useAuth();
  useEffect(() => {
    if (!user || seen) return;
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setSeen(true);
  }, [user, seen]);
  // Wait until the user has dealt with the cookie banner — otherwise the
  // banner sits on top and the tutorial highlights an element behind it.
  const [cookiesOk, setCookiesOk] = useState(() => {
    try { return !!localStorage.getItem('cookie_consent'); } catch { return true; }
  });
  useEffect(() => {
    if (cookiesOk) return;
    const t = setInterval(() => {
      try {
        if (localStorage.getItem('cookie_consent')) setCookiesOk(true);
      } catch (e) { /* ignore */ }
    }, 500);
    return () => clearInterval(t);
  }, [cookiesOk]);

  // ───────── Eligible NS matches (for step 2) ─────────
  const eligibleMatches = useMemo(() => {
    if (!Array.isArray(matches)) return [];
    return matches.filter(isNotStarted);
  }, [matches]);

  // Helper: NS matches whose DOM row is currently mounted (regardless of
  // viewport position). Used as the picking pool — the highlight will then
  // auto-scrollIntoView to bring the chosen row inside the safe viewport.
  const getMountedNS = useCallback(() => {
    if (typeof document === 'undefined') return [];
    const renderedIds = new Set();
    document.querySelectorAll('[data-testid^="match-row-"]').forEach(el => {
      const tid = el.getAttribute('data-testid') || '';
      const m = tid.match(/^match-row-(\d+)$/);
      if (!m) return;
      const r = el.getBoundingClientRect();
      if (r.height <= 0) return; // not actually painted
      renderedIds.add(parseInt(m[1], 10));
    });
    return eligibleMatches.filter(m => renderedIds.has(m.fixture_id));
  }, [eligibleMatches]);

  // ───────── Step decision ─────────
  //   'match' — at least ONE NS match exists today (regardless of whether
  //             it's currently in viewport — we'll scroll it into view).
  //   'date'  — zero NS matches today (e.g., it's 23:30 and everything is
  //             finished/live/postponed). We redirect to tomorrow's pill.
  const mode = eligibleMatches.length > 0 ? 'match' : 'date';

  // ───────── Step 2 (match) state ─────────
  const [targetMatchId, setTargetMatchId] = useState(null);
  const targetMatchIdRef = useRef(null);
  targetMatchIdRef.current = targetMatchId;

  // Reset match target whenever the selectedDate changes (e.g., the user
  // followed step 1 and Dashboard re-loaded with the next day's matches).
  useEffect(() => { setTargetMatchId(null); }, [selectedDate]);

  // Initial random pick: try the mounted NS pool with retries. If no row is
  // mounted yet (Virtuoso may still be initializing), we keep retrying for a
  // few seconds, then give up and switch to 'date' mode via mode change.
  useEffect(() => {
    if (seen || !cookiesOk || isLoading || mode !== 'match' || targetMatchId) return;
    let attempts = 0;
    let cancelled = false;
    const tryPick = () => {
      if (cancelled) return;
      const pool = getMountedNS();
      if (pool.length) {
        const chosen = pickRandom(pool);
        if (chosen) setTargetMatchId(chosen.fixture_id);
        return;
      }
      if (attempts++ < 20) setTimeout(tryPick, 250);
    };
    tryPick();
    return () => { cancelled = true; };
  }, [seen, cookiesOk, isLoading, mode, targetMatchId, getMountedNS]);

  // Re-pick if the chosen match leaves the NS pool while the tutorial is up.
  useEffect(() => {
    if (seen || mode !== 'match') return;
    const tick = setInterval(() => {
      const still = eligibleMatches.some(m => m.fixture_id === targetMatchIdRef.current);
      if (!still) {
        const next = pickRandom(getMountedNS());
        if (next) setTargetMatchId(next.fixture_id);
      }
    }, 15000);
    return () => clearInterval(tick);
  }, [seen, mode, eligibleMatches, getMountedNS]);

  // ───────── Resolve highlight target (DOM selector for the current step) ─────────
  const targetSelector = useMemo(() => {
    if (seen || !cookiesOk) return null;
    if (mode === 'match') {
      return targetMatchId ? `[data-testid="match-row-${targetMatchId}"]` : null;
    }
    // mode === 'date'  → highlight the next-day pill
    const nd = nextDay(selectedDate);
    return nd ? `[data-testid="date-${nd}"]` : null;
  }, [seen, cookiesOk, mode, targetMatchId, selectedDate]);

  // ───────── Measure & keep the highlight rect in sync ─────────
  const [rect, setRect] = useState(null);
  const measure = useCallback(() => {
    if (!targetSelector) { setRect(null); return; }
    const el = document.querySelector(targetSelector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [targetSelector]);

  useEffect(() => {
    if (seen || !targetSelector) { setRect(null); return; }
    measure();
    const t1 = setTimeout(measure, 200);
    const t2 = setTimeout(measure, 600);
    const t3 = setTimeout(measure, 1200);
    const onScroll = () => measure();
    const onResize = () => measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [targetSelector, seen, measure]);

  // Auto-scroll the highlighted match row into the middle of the screen.
  // Only for step 2 (match) — the date pills are in a horizontal bar at the
  // top so they're always visible.
  useEffect(() => {
    if (seen || mode !== 'match' || !targetSelector) return;
    const el = document.querySelector(targetSelector);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    if (r.top < 80 || r.bottom > vh - 96) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(measure, 400);
    }
  }, [targetSelector, seen, mode, measure]);

  // ───────── Capture the click on the match row to mark tutorial as seen ─────────
  // We attach this in capture phase BEFORE the row's own onClick fires so the
  // localStorage flag is persisted even though Dashboard.jsx (and therefore
  // this component) unmounts right after the navigation.
  useEffect(() => {
    if (seen || mode !== 'match' || !targetMatchId) return;
    const el = document.querySelector(`[data-testid="match-row-${targetMatchId}"]`);
    if (!el) return;
    const onClick = () => {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
      setSeen(true);
    };
    el.addEventListener('click', onClick, { capture: true });
    return () => el.removeEventListener('click', onClick, { capture: true });
  }, [targetMatchId, seen, mode]);

  // ───────── Bail-out conditions ─────────
  if (seen) return null;
  if (authLoading || user) return null;
  if (!cookiesOk) return null;
  // While the next-day matches are loading after the user clicked a date pill,
  // we don't have rect yet — keep showing a gentle "loading next day" hint.
  if (!rect) {
    if (isLoading && mode === 'match') {
      return (
        <div
          className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6 pointer-events-auto"
          data-testid="onboarding-tutorial-loading"
        >
          <div className="bg-[#1a1a1a] border border-[#F5A623]/40 rounded-2xl p-6 max-w-sm text-center shadow-2xl">
            <p className="text-[#F5A623] text-xs font-bold uppercase tracking-widest mb-2">Tutorial</p>
            <p className="text-white text-base font-bold">Carico le partite del giorno…</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // ───────── Layout: 4 masks around the hole + arrow + text card ─────────
  const PAD = 6;
  const hole = {
    top: rect.top - PAD,
    left: Math.max(0, rect.left - PAD),
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const placeBelow = hole.top + hole.height + 200 < vh;
  const arrowTop = placeBelow ? hole.top + hole.height + 4 : hole.top - 56;
  const arrowRotate = placeBelow ? 0 : 180;
  const cardTop = placeBelow ? hole.top + hole.height + 60 : hole.top - 210;

  // In step 2 ('match') keep the masks lighter so the user can still glance
  // at the rest of the schedule (palinsesto) and understand the app shows a
  // full list of matches — not just the highlighted one.
  const maskClass = mode === 'match'
    ? 'absolute bg-black/45 pointer-events-auto cursor-not-allowed'
    : 'absolute bg-black/75 backdrop-blur-[2px] pointer-events-auto cursor-not-allowed';

  const card = mode === 'date'
    ? {
        kicker: 'Benvenuto su Rvincente',
        title: 'Per oggi è troppo tardi',
        body: (
          <>Le partite di oggi sono già iniziate o terminate. <span className="text-[#F5A623] font-bold">Tocca il giorno successivo</span> qui sopra per vedere le partite in programma e i nostri pronostici.</>
        ),
      }
    : {
        kicker: 'Benvenuto su Rvincente',
        title: 'Clicca su una partita non ancora iniziata',
        body: (
          <>Per vedere il pronostico dei nostri esperti, apri una qualsiasi partita in programma. Ne abbiamo evidenziata una per te qui sotto: <span className="text-[#F5A623] font-bold">tocca il riquadro arancione</span> per continuare.</>
        ),
      };

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none" data-testid="onboarding-tutorial" data-mode={mode}>
      {/* 4 masking quadrants — capture clicks but let vertical scroll through */}
      <div
        className={maskClass}
        style={{ top: 0, left: 0, width: '100%', height: Math.max(0, hole.top), touchAction: 'pan-y' }}
        data-testid="onboarding-mask-top"
      />
      <div
        className={maskClass}
        style={{ top: hole.top + hole.height, left: 0, width: '100%', height: Math.max(0, vh - (hole.top + hole.height)), touchAction: 'pan-y' }}
        data-testid="onboarding-mask-bottom"
      />
      <div
        className={maskClass}
        style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height, touchAction: 'pan-y' }}
      />
      <div
        className={maskClass}
        style={{ top: hole.top, left: hole.left + hole.width, width: Math.max(0, vw - (hole.left + hole.width)), height: hole.height, touchAction: 'pan-y' }}
      />

      {/* Highlight ring around the hole (purely decorative — does not block clicks) */}
      <div
        className="absolute pointer-events-none animate-[onboard-pulse_1.4s_ease-in-out_infinite] rounded-xl"
        style={{
          top: hole.top, left: hole.left, width: hole.width, height: hole.height,
          boxShadow: '0 0 0 3px #F5A623, 0 0 24px 6px rgba(245,166,35,0.55)',
        }}
      />

      {/* Animated arrow pointing to the target */}
      <div
        className="absolute pointer-events-none select-none"
        style={{
          left: Math.min(vw - 64, Math.max(16, hole.left + hole.width / 2 - 24)),
          top: arrowTop,
          transform: `rotate(${arrowRotate}deg)`,
        }}
        data-testid="onboarding-arrow"
      >
        <div className="animate-[onboard-bounce_1s_ease-in-out_infinite]">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path
              d="M24 4 L24 38 M12 26 L24 38 L36 26"
              stroke="#F5A623"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: 'drop-shadow(0 0 8px rgba(245,166,35,0.9))' }}
            />
          </svg>
        </div>
      </div>

      {/* Text card */}
      <div
        className="absolute left-1/2 -translate-x-1/2 px-4 w-[min(420px,calc(100vw-32px))]"
        style={{ top: Math.max(16, Math.min(vh - 220, cardTop)) }}
      >
        <div
          className="bg-[#1a1a1a] border border-[#F5A623]/50 rounded-2xl p-4 shadow-2xl pointer-events-auto"
          data-testid="onboarding-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#F5A623] animate-pulse" />
            <p className="text-[#F5A623] text-[10px] font-black uppercase tracking-[0.18em]">{card.kicker}</p>
          </div>
          <h3 className="text-white text-base font-extrabold mb-1.5 leading-tight">{card.title}</h3>
          <p className="text-gray-300 text-[13px] leading-snug">{card.body}</p>
        </div>
      </div>

      <style>{`
        @keyframes onboard-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        @keyframes onboard-pulse {
          0%, 100% { box-shadow: 0 0 0 3px #F5A623, 0 0 24px 6px rgba(245,166,35,0.55); }
          50% { box-shadow: 0 0 0 4px #F5A623, 0 0 36px 10px rgba(245,166,35,0.85); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingTutorial;
